<template>
  <v-container fluid>
    <div class="d-flex mb-2">
      <h2 style="font-weight: 400">กำหนดสิทธิ์การลา</h2>
    </div>
    <v-card outlined class="pa-4 mb-4">
      <h4 style="font-weight: 500">วันเริ่มสิทธิ์การลา</h4>
      <v-divider class="my-2"></v-divider>
      <p class="body-2 mb-1">วันเริ่มสิทธิ์การลา (สำหรับพนักงานรายเดือน)</p>
      <div class="d-flex">
        <div class="mr-4">
          <label class="caption">วันที่</label>
          <v-select
            v-model="configLeavePeriodStartDate.monthly.day"
            :items="monthlyDays"
            outlined
            dense
            style="width: 100px"
            no-data-text="โปรดเลือกเดือนก่อน"
          />
        </div>
        <div>
          <label class="caption">เดือน</label>
          <v-select
            v-model="configLeavePeriodStartDate.monthly.month"
            :items="months"
            outlined
            dense
            style="width: 200px"
          />
        </div>
      </div>
      <p class="body-2 mb-1">วันเริ่มสิทธิ์การลา (สำหรับพนักงานรายวัน)</p>
      <div class="d-flex">
        <div class="mr-4">
          <label class="caption">วันที่</label>
          <v-select
            v-model="configLeavePeriodStartDate.daily.day"
            :items="dailyDays"
            outlined
            dense
            style="width: 100px"
            no-data-text="โปรดเลือกเดือนก่อน"
          />
        </div>
        <div>
          <label class="caption">เดือน</label>
          <v-select
            v-model="configLeavePeriodStartDate.daily.month"
            :items="months"
            outlined
            dense
            style="width: 200px"
          />
        </div>
      </div>
      <div>
        <v-btn
          class="mr-4"
          color="primary"
          :disabled="
            !isConfigLeavePeriodStartDateDataChange ||
            (uiCtrl &&
              !uiCtrl.isEnable(
                'btn:::company_config:leave_period_start_date:update'
              ))
          "
          @click="onSaveConfigLeavePeriodStartDate"
          >บันทึก</v-btn
        >
        <v-btn @click="resetConfigLeavePeriodStartDate">ยกเลิก</v-btn>
      </div>
    </v-card>
    <v-card outlined class="pa-4 mb-8">
      <h4 style="font-weight: 500">การคำนวณสิทธิ์การลาตามอายุงาน</h4>
      <v-divider class="mt-2 mb-4"></v-divider>
      <div>
        <div class="d-flex" style="height: 50px">
          <v-checkbox
            v-model="configLeaveTenurePolicy.is_one_year_required"
            class="mt-0 mr-6 d-inline-block"
            label="ครบปี"
            dense
            hide-details
          >
            <template v-slot:label>
              <span style="font-size: 14px">ครบปี</span>
            </template>
          </v-checkbox>
          <v-checkbox
            v-model="configLeaveTenurePolicy.is_carry_forward_enabled"
            class="mt-0 d-inline-block"
            label="รวมยอดคงเหลือจากปีก่อน"
            dense
            hide-details
          >
            <template v-slot:label>
              <span style="font-size: 14px">รวมยอดคงเหลือจากปีก่อน</span>
            </template>
          </v-checkbox>
          <div
            class="ml-4 d-flex align-center"
            v-if="configLeaveTenurePolicy.is_carry_forward_enabled"
          >
            <div style="width: 80px">
              <v-text-field
                v-model.number="configLeaveTenurePolicy.max_carry_forward_days"
                :rules="[
                  validationRules.positiveInteger,
                  validationRules.maxDays(365),
                ]"
                type="number"
                min="0"
                max="365"
                outlined
                dense
                hide-details
              ></v-text-field>
            </div>
            <span class="ml-2 body-2 grey--text text--darken-3">
              สูงสุด / วัน
            </span>
          </div>
        </div>
        <div class="mt-4">
          <p class="ml-1 mb-0" style="font-size: 13px">นับอายุการทำงานจาก</p>
          <div class="d-flex">
            <v-radio-group
              v-model="configLeaveTenurePolicy.tenure_base_date_type"
              dense
              row
              class="mt-1"
              hide-details
            >
              <v-radio label="วันเริ่มใช้งาน" value="start_date">
                <template v-slot:label>
                  <span style="font-size: 14px">วันเริ่มใช้งาน</span>
                </template>
              </v-radio>
              <v-radio label="วันผ่านทดลองงาน" value="probation_passed_date">
                <template v-slot:label>
                  <span style="font-size: 14px">วันผ่านทดลองงาน</span>
                </template>
              </v-radio>
            </v-radio-group>
          </div>
        </div>
      </div>
      <v-divider class="mt-4 mb-4"></v-divider>
      <v-simple-table>
        <template v-slot:default>
          <thead>
            <tr>
              <th class="text-left">อายุงานครบ</th>
              <th class="text-left">หน่วย</th>
              <th class="text-left">สิทธิ์ลา (ชั่วโมง / วัน)</th>
              <th>
                <v-btn
                  @click="handleAddLeaveTenureRule"
                  outlined
                  small
                  color="primary"
                >
                  <v-icon small left>mdi-plus</v-icon>
                  เพิ่ม
                </v-btn>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="leaveTenureRule in configLeaveTenurePolicy.leave_tenure_rules"
              :key="leaveTenureRule.display_order"
            >
              <td>{{ leaveTenureRule.min_tenure }}</td>
              <td>
                {{ leaveTenureRule.tenure_unit === 'month' ? 'เดือน' : 'ปี' }}
              </td>
              <td>
                <span v-if="leaveTenureRule.leave_amounts.annual.days"
                  >{{ leaveTenureRule.leave_amounts.annual.days }} วัน</span
                >
                <span v-if="leaveTenureRule.leave_amounts.annual.hours">
                  {{ leaveTenureRule.leave_amounts.annual.hours }} ชั่วโมง
                </span>
              </td>
              <td>
                <v-btn
                  icon
                  small
                  @click="handleEditLeaveTenureRule(leaveTenureRule)"
                >
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
                <v-btn
                  icon
                  small
                  @click="handleDeleteLeaveTenureRule(leaveTenureRule)"
                >
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
      <div class="mt-6">
        <v-btn
          class="mr-4"
          color="primary"
          :disabled="
            !isConfigLeaveTenurePolicyDataChange ||
            (uiCtrl &&
              !uiCtrl.isEnable(
                'btn:::company_config:leave_tenure_policy:update'
              ))
          "
          @click="onSaveConfigLeaveTenurePolicy"
          >บันทึก</v-btn
        >
        <v-btn @click="resetConfigLeaveTenurePolicy">ยกเลิก</v-btn>
      </div>

      <v-dialog v-model="dialogAddEditLeaveTenureRule" max-width="680">
        <v-card style="position: relative">
          <v-btn
            icon
            style="position: absolute; top: 8px; right: 8px"
            @click="dialogAddEditLeaveTenureRule = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>

          <v-card-title class="text-h5">
            {{
              addEditLeaveTenureRule.type === 'add' ? 'เพิ่ม' : 'แก้ไข'
            }}สิทธิ์การลาตามอายุงาน
          </v-card-title>
          <v-card-text class="mt-4 d-flex justify-space-between">
            <div style="width: 120px">
              <v-text-field
                v-model="addEditLeaveTenureRule.min_tenure"
                :rules="[(v) => !!v || 'กรุณาระบุอายุงานครบ']"
                :disabled="addEditLeaveTenureRule.type === 'edit'"
                type="number"
                min="1"
                max="20"
                outlined
                dense
                label="อายุงานครบ"
                hide-details
              ></v-text-field>
            </div>
            <div style="width: 120px">
              <v-select
                v-model="addEditLeaveTenureRule.tenure_unit"
                :disabled="addEditLeaveTenureRule.type === 'edit'"
                :rules="[(v) => !!v || 'กรุณาระบบหน่วย']"
                :items="[
                  { text: 'เดือน', value: 'month' },
                  { text: 'ปี', value: 'year' },
                ]"
                outlined
                dense
                label="หน่วย"
                hide-details
              />
            </div>
            <div style="width: 150px">
              <v-text-field
                v-model="addEditLeaveTenureRule.leave_amounts.annual.days"
                type="number"
                min="1"
                max="30"
                outlined
                dense
                label="สิทธิ์ (วัน)"
                hide-details
              ></v-text-field>
            </div>
            <div style="width: 170px">
              <v-text-field
                v-model="addEditLeaveTenureRule.leave_amounts.annual.hours"
                type="number"
                min="0"
                max="23"
                outlined
                dense
                label="สิทธิ์ (ชั่วโมง)"
                hide-details
              ></v-text-field>
            </div>
          </v-card-text>
          <v-card-actions class="pb-4">
            <v-spacer></v-spacer>
            <v-btn
              :disabled="
                !addEditLeaveTenureRule.min_tenure ||
                addEditLeaveTenureRule.min_tenure === '0' ||
                !addEditLeaveTenureRule.tenure_unit ||
                ((!addEditLeaveTenureRule.leave_amounts.annual.days ||
                  addEditLeaveTenureRule.leave_amounts.annual.days === '0') &&
                  (!addEditLeaveTenureRule.leave_amounts.annual.hours ||
                    addEditLeaveTenureRule.leave_amounts.annual.hours === '0'))
              "
              color="primary darken-1"
              @click="handleAddEditLeaveTenureRuleSubmit"
            >
              {{
                addEditLeaveTenureRule.type === 'add'
                  ? 'เพิ่มสิทธิ์'
                  : 'แก้ไขสิทธิ์'
              }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
    <ConfirmDialog
      :dialog="confirmDialogDataChange"
      :loading="confirmDialogDataChangeLoading"
      :dialogData="confirmDialogDataChangeData"
      @cancelConfirmDialog="cancelConfirmDialogDataChange"
      @submitConfirmDialog="submitConfirmDialogDataChange"
    />
  </v-container>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'

import ConfirmDialog from '@/components/ConfirmDialog.vue'

export default {
  name: 'LeaveEntitlement',
  components: {
    ConfirmDialog,
  },
  data() {
    return {
      validationRules: {
        required: (v) => !!v || 'กรุณากรอกข้อมูล',
        positiveInteger: (v) => {
          if (!v) return true
          const num = parseInt(v)
          return (
            (!isNaN(num) && num > 0 && Number.isInteger(num)) ||
            'กรุณากรอกตัวเลขจำนวนเต็มบวก'
          )
        },
        maxDays: (max) => (v) => {
          if (!v) return true
          const num = parseInt(v)
          return num <= max || `กรุณากรอกไม่เกิน ${max} วัน`
        },
        tenure: (v) => {
          if (!v) return 'กรุณาระบุอายุงานครบ'
          const num = parseInt(v)
          return (num >= 1 && num <= 50) || 'อายุงานต้องอยู่ระหว่าง 1-50'
        },
      },
      addEditLeaveTenureRule: {
        type: 'add',
        display_order: '',
        min_tenure: '',
        tenure_unit: '',
        leave_amounts: {
          annual: {
            days: '',
            hours: '',
          },
        },
      },
      dialogAddEditLeaveTenureRule: false,
      months: [
        { text: 'มกราคม', value: 1 },
        { text: 'กุมภาพันธ์', value: 2 },
        { text: 'มีนาคม', value: 3 },
        { text: 'เมษายน', value: 4 },
        { text: 'พฤษภาคม', value: 5 },
        { text: 'มิถุนายน', value: 6 },
        { text: 'กรกฎาคม', value: 7 },
        { text: 'สิงหาคม', value: 8 },
        { text: 'กันยายน', value: 9 },
        { text: 'ตุลาคม', value: 10 },
        { text: 'พฤศจิกายน', value: 11 },
        { text: 'ธันวาคม', value: 12 },
      ],
      configLeaveTenurePolicy: {
        leave_tenure_rules: [],
      },
      configLeaveTenurePolicyInit: {
        leave_tenure_rules: [],
      },
      configLeavePeriodStartDate: {
        daily: {},
        monthly: {},
      },
      configLeavePeriodStartDateInit: {
        daily: {},
        monthly: {},
      },
      confirmDialogDataChange: false,
      confirmDialogDataChangeLoading: false,
      confirmDialogDataChangeData: {},
      timeout: null,
    }
  },
  computed: {
    uiCtrl() {
      return this.$store.state.uiCtrl
    },
    isConfigLeaveTenurePolicyDataChange() {
      return !_.isEqual(
        this.configLeaveTenurePolicy,
        this.configLeaveTenurePolicyInit
      )
    },
    isConfigLeavePeriodStartDateDataChange() {
      return !_.isEqual(
        this.configLeavePeriodStartDate,
        this.configLeavePeriodStartDateInit
      )
    },
    monthlyDays() {
      if (!this.configLeavePeriodStartDate?.monthly?.month) return []
      const currentYear = new Date().getFullYear()
      const max = new Date(
        currentYear,
        this.configLeavePeriodStartDate.monthly.month,
        0
      ).getDate()
      return Array.from({ length: max }, (_, i) => i + 1)
    },
    dailyDays() {
      if (!this.configLeavePeriodStartDate?.daily?.month) return []
      const currentYear = new Date().getFullYear()
      const max = new Date(
        currentYear,
        this.configLeavePeriodStartDate.daily.month,
        0
      ).getDate()
      return Array.from({ length: max }, (_, i) => i + 1)
    },
  },
  watch: {
    uiCtrl(value) {
      if (
        this.uiCtrl &&
        !this.uiCtrl.isEnable('menu:::company_config:leave_entitlements')
      ) {
        this.$router.push({ name: 'Home' })
      } else if (value) {
        this.loadData()
      }
    },
  },
  created() {
    this.$store.commit('SET_BREADCRUMBS', [
      { text: 'หน้าหลัก', disabled: false, to: '/' },
      {
        text: 'กำหนดสิทธิ์การลา',
        disabled: true,
      },
    ])

    if (
      this.uiCtrl &&
      this.uiCtrl.isEnable('menu:::company_config:leave_entitlements')
    ) {
      this.loadData()
    }
  },
  methods: {
    ...mapActions({
      fetchConfigLeaveTenurePolicy: 'leave/fetchConfigLeaveTenurePolicy',
      editConfigLeaveTenurePolicy: 'leave/editConfigLeaveTenurePolicy',
      fetchConfigLeavePeriodStartDate: 'leave/fetchConfigLeavePeriodStartDate',
      editConfigLeavePeriodStartDate: 'leave/editConfigLeavePeriodStartDate',
    }),
    sortLeaveTenureRules() {
      this.configLeaveTenurePolicy.leave_tenure_rules.sort((a, b) => {
        // แปลงเป็นเดือนเพื่อเปรียบเทียบ (ปี * 12 + เดือน)
        const aMonths =
          a.tenure_unit === 'year'
            ? parseInt(a.min_tenure) * 12
            : parseInt(a.min_tenure)
        const bMonths =
          b.tenure_unit === 'year'
            ? parseInt(b.min_tenure) * 12
            : parseInt(b.min_tenure)

        return aMonths - bMonths
      })
    },
    handleAddEditLeaveTenureRuleSubmit() {
      if (this.addEditLeaveTenureRule.type === 'add') {
        const result = this.configLeaveTenurePolicy.leave_tenure_rules.find(
          (item) =>
            item.min_tenure ===
              parseInt(this.addEditLeaveTenureRule.min_tenure) &&
            item.tenure_unit === this.addEditLeaveTenureRule.tenure_unit
        )

        if (result) {
          result.leave_amounts.annual.days =
            this.addEditLeaveTenureRule.leave_amounts.annual.days
          result.leave_amounts.annual.hours =
            this.addEditLeaveTenureRule.leave_amounts.annual.hours
        } else {
          this.configLeaveTenurePolicy.leave_tenure_rules.push({
            ...this.addEditLeaveTenureRule,
            leave_amounts: {
              annual: {
                ...this.addEditLeaveTenureRule.leave_amounts.annual,
              },
            },
          })
        }
      } else {
        const index = this.configLeaveTenurePolicy.leave_tenure_rules.findIndex(
          (item) =>
            item.display_order === this.addEditLeaveTenureRule.display_order
        )
        if (index > -1) {
          this.configLeaveTenurePolicy.leave_tenure_rules.splice(index, 1, {
            ...this.addEditLeaveTenureRule,
          })
        }
      }

      this.sortLeaveTenureRules()
      this.dialogAddEditLeaveTenureRule = false
    },
    handleAddLeaveTenureRule() {
      this.addEditLeaveTenureRule = {
        type: 'add',
        display_order: '',
        min_tenure: '',
        tenure_unit: '',
        leave_amounts: {
          annual: {
            days: '',
            hours: '',
          },
        },
      }
      this.dialogAddEditLeaveTenureRule = true
    },
    handleEditLeaveTenureRule(leaveTenureRule) {
      this.addEditLeaveTenureRule = {
        ...leaveTenureRule,
        type: 'edit',
        leave_amounts: {
          annual: {
            ...leaveTenureRule.leave_amounts.annual,
          },
        },
      }
      this.dialogAddEditLeaveTenureRule = true
    },
    handleDeleteLeaveTenureRule(leaveTenureRule) {
      const index =
        this.configLeaveTenurePolicy.leave_tenure_rules.indexOf(leaveTenureRule)
      if (index > -1) {
        this.configLeaveTenurePolicy.leave_tenure_rules.splice(index, 1)
      }
    },
    onSaveConfigLeaveTenurePolicy() {
      const { company_uuid } = this.$store.state.authentication.company

      this.$store.commit('SET_LOADING', true)

      this.editConfigLeaveTenurePolicy({
        company_uuid,
        body: {
          ...this.configLeaveTenurePolicy,
          leave_tenure_rules:
            this.configLeaveTenurePolicy.leave_tenure_rules.map(
              (item, index) => ({
                display_order: index + 1,
                min_tenure: parseInt(item.min_tenure),
                tenure_unit: item.tenure_unit,
                leave_amounts: {
                  annual: {
                    days: item.leave_amounts.annual.days
                      ? parseInt(item.leave_amounts.annual.days)
                      : 0,
                    hours: item.leave_amounts.annual.hours
                      ? parseInt(item.leave_amounts.annual.hours)
                      : 0,
                  },
                },
              })
            ),
        },
      })
        .then(() => {
          this.$store.commit('SET_LOADING', false)

          this.configLeaveTenurePolicy = _.cloneDeep(
            this.$store.state.leave.configLeaveTenurePolicy
          )
          this.configLeaveTenurePolicyInit = _.cloneDeep(
            this.$store.state.leave.configLeaveTenurePolicy
          )

          const snackbar = {
            show: true,
            color: 'success',
            timeout: 4000,
            message: 'บันทึกข้อมูลสำเร็จ',
          }

          this.$store.commit('SET_SNACKBAR', snackbar)
        })
        .catch((error) => {
          this.$store.commit('SET_LOADING', false)

          const snackbar = {
            show: true,
            color: 'error',
            timeout: -1,
            message: error?.response?.data?.message || 'Something went wrong!',
          }
          this.$store.commit('SET_SNACKBAR', snackbar)
        })
    },
    onSaveConfigLeavePeriodStartDate() {
      const { company_uuid } = this.$store.state.authentication.company

      this.$store.commit('SET_LOADING', true)

      this.editConfigLeavePeriodStartDate({
        company_uuid,
        body: {
          ...this.configLeavePeriodStartDate,
        },
      })
        .then(() => {
          this.$store.commit('SET_LOADING', false)

          const responseData =
            this.$store.state.leave.configLeavePeriodStartDate

          this.configLeavePeriodStartDateInit = JSON.parse(
            JSON.stringify(responseData)
          )

          const snackbar = {
            show: true,
            color: 'success',
            timeout: 4000,
            message: 'บันทึกข้อมูลสำเร็จ',
          }

          this.$store.commit('SET_SNACKBAR', snackbar)
        })
        .catch((error) => {
          this.$store.commit('SET_LOADING', false)

          const snackbar = {
            show: true,
            color: 'error',
            timeout: -1,
            message: error?.response?.data?.message || 'Something went wrong!',
          }
          this.$store.commit('SET_SNACKBAR', snackbar)
        })
    },
    resetConfigLeaveTenurePolicy() {
      this.configLeaveTenurePolicy = JSON.parse(
        JSON.stringify(this.configLeaveTenurePolicyInit)
      )
    },
    resetConfigLeavePeriodStartDate() {
      this.configLeavePeriodStartDate = JSON.parse(
        JSON.stringify(this.configLeavePeriodStartDateInit)
      )
    },
    async loadData() {
      try {
        const { company_uuid } = this.$store.state.authentication.company

        this.$store.state.loading = true

        const payload = {
          company_uuid,
        }

        await Promise.all([
          this.fetchConfigLeaveTenurePolicy(payload),
          this.fetchConfigLeavePeriodStartDate(payload),
        ])

        this.configLeaveTenurePolicy = JSON.parse(
          JSON.stringify(this.$store.state.leave.configLeaveTenurePolicy)
        )
        this.configLeaveTenurePolicyInit = JSON.parse(
          JSON.stringify(this.$store.state.leave.configLeaveTenurePolicy)
        )

        this.configLeavePeriodStartDate = JSON.parse(
          JSON.stringify(this.$store.state.leave.configLeavePeriodStartDate)
        )
        this.configLeavePeriodStartDateInit = JSON.parse(
          JSON.stringify(this.$store.state.leave.configLeavePeriodStartDate)
        )
      } catch (error) {
        const snackbar = {
          show: true,
          color: 'error',
          timeout: -1,
          message: error?.response?.data?.message || 'Something went wrong!',
        }
        this.$store.commit('SET_SNACKBAR', snackbar)
      } finally {
        this.$store.commit('SET_LOADING', false)
      }
    },
    cancelConfirmDialogDataChange() {
      this.confirmDialogDataChange = false
    },
    submitConfirmDialogDataChange(item) {
      this.confirmDialogDataChange = false
      this.nextRoute(item.data)
    },
    nextRoute(next) {
      this.timeout = setTimeout(() => {
        next()
      }, 200)
    },
  },
  beforeRouteLeave(to, from, next) {
    if (
      this.isConfigLeaveTenurePolicyDataChange ||
      this.isConfigLeavePeriodStartDateDataChange
    ) {
      this.confirmDialogDataChangeData = {
        title: 'ต้องการออกจากหน้านี้ใช่ไหม',
        text: 'คุณยังไม่ได้บันทึกข้อมูลของคุณ คุณแน่ใจหรือไม่ว่าต้องการออกโดยที่ยังไม่บันทึกข้อมูล',
        value: '',
        item: {
          type: 'next',
          data: next,
        },
      }
      this.confirmDialogDataChange = true
    } else {
      this.nextRoute(next)
    }
  },
  beforeDestroy() {
    clearTimeout(this.timeout)
  },
}
</script>
